import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import CombinedTextField from '../../components/CombinedTextField';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import { MAX_AMOUNT_LIMIT, MIN_AMOUNT_LIMIT } from '../../constants/numberLimit';
import analytics from '../../lib/analytics';
import useDefaultCurrency from '../../lib/hooks/useDefaultCurrency';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { updateValue } from '../../utils/utils';

export const unemployedSalaryPayload = payloadFactory.createAddPageDataPayload('currentSalary', {
  currency: 'USD',
  amount: '0',
});

const CurrentSalaryPage = ({ initialCurrentSalary, dispatchAddPageData, dispatchWizardNav }) => {
  const [defaultCurrency, currencies] = useDefaultCurrency();
  const [salaryCurrency, setSalaryCurrency] = useState(
    initialCurrentSalary?.currency || defaultCurrency,
  );
  const [salaryAmount, setSalaryAmount] = useState(initialCurrentSalary?.amount || '');

  const isInvalid = salaryCurrency.length === 0 || salaryAmount.length === 0;

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid) {
      return;
    }

    analytics.sendEvent('Click - Next', 'current-salary');

    const payload = payloadFactory.createAddPageDataPayload('currentSalary', {
      currency: salaryCurrency,
      amount: salaryAmount,
    });
    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const changeCurrency = event => {
    setSalaryCurrency(event.target.value);
  };

  const renderAnnualSalaryHelper = () => {
    const annualAmount = Number.parseFloat(salaryAmount) * 12;

    if (isNaN(annualAmount) || annualAmount === 0 || salaryCurrency.length === 0) {
      return null;
    }

    const formattedAnnualAmount = annualAmount.toLocaleString('en-US', {
      style: 'currency',
      currency: salaryCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return (
      <div className="row-spacer">
        That means you earn&nbsp;
        <b>{formattedAnnualAmount} a year.</b>
      </div>
    );
  };

  return (
    <PageLayout
      title="How much is your gross monthly salary?"
      helpText="Check your payslip and tell us your gross salary, which is the number before tax is
                deducted. You should also exclude any bonuses for now."
    >
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col col--11/16-small">
            <CombinedTextField>
              <Select
                id="salary-currency"
                value={salaryCurrency}
                onChange={changeCurrency}
                options={currencies}
              />

              <InputField
                type="text"
                inputMode="numeric"
                pattern="\d*"
                id="current-salary"
                placeholder="Enter amount"
                value={salaryAmount}
                onChange={e => updateValue(e, setSalaryAmount, MAX_AMOUNT_LIMIT)}
                min={MIN_AMOUNT_LIMIT}
                max={MAX_AMOUNT_LIMIT}
              />
            </CombinedTextField>
          </div>
        </div>
        {renderAnnualSalaryHelper()}
        <div className="row-spacer">
          <Button type="submit" isDisabled={isInvalid}>
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

CurrentSalaryPage.propTypes = {
  initialCurrentSalary: PropTypes.shape({
    currency: PropTypes.string,
    amount: PropTypes.string,
  }),
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
};

CurrentSalaryPage.defaultProps = {
  initialCurrentSalary: {
    currency: '',
    amount: '',
  },
};

export const mapStateToProps = state => ({
  initialCurrentSalary: state.builder.ui.currentSalary,
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { CurrentSalaryPage };

export default connect(mapStateToProps, mapDispatchToProps)(CurrentSalaryPage);
